import { useState } from "react";
import { Link } from "react-router-dom";
import { Check, Copy, ExternalLink, Eye, Waves, Zap, Shield } from "lucide-react";
import Layout from "@/components/Layout";
import SEOHead from "@/components/SEOHead";
import { DISCOUNT_CODE, DISCOUNT_PERCENT, PARTNER_LINK } from "@/lib/constants";


const CANONICAL = "https://heilys.info/gen30-vision";

// Sobald das Review-Video live ist: hier die YouTube-Video-ID eintragen (z. B. "dQw4w9WgXcQ").
// Solange null, wird der Video-Embed nicht gerendert.
const YOUTUBE_VIDEO_ID: string | null = "cRDz5XgZGPw";

const SPECS: { label: string; value: string }[] = [
  { label: "LEDs", value: "36 Dual-Chip-LEDs" },
  { label: "Leistung", value: "30 W" },
  { label: "Wellenlängen", value: "630 · 660 · 670 · 810 · 830 · 850 nm" },
  { label: "Sondermodus", value: "Vision-Modus 670 nm" },
  { label: "Betriebsmodi", value: "Kombi · Puls · Vision" },
  { label: "Bestrahlungsstärke", value: "bis 95 mW/cm² (7,5 cm)" },
  { label: "Abstrahlwinkel", value: "16°" },
  { label: "EMF", value: "sehr niedrig (Low-EMF)" },
  { label: "UVP / Straßenpreis", value: "199 € / ~159 €" },
];

const WAVELENGTHS: { nm: string; range: "Rot" | "NIR"; note: string }[] = [
  { nm: "630 nm", range: "Rot", note: "Sichtbares Rotlicht, oberflächennaher Bereich" },
  { nm: "660 nm", range: "Rot", note: "Tiefer eindringendes Rotlicht, häufig genutzte Wellenlänge" },
  { nm: "670 nm", range: "Rot", note: "Vision-Modus für die Anwendung im Augenbereich (laut Hersteller ohne Schutzbrille)" },
  { nm: "810 nm", range: "NIR", note: "Nahinfrarot, dringt tiefer in Gewebe ein" },
  { nm: "830 nm", range: "NIR", note: "Nahinfrarot-Bereich für tieferliegende Strukturen" },
  { nm: "850 nm", range: "NIR", note: "Häufigste NIR-Wellenlänge in Heimgeräten" },
];

const FAQ: { q: string; a: string }[] = [
  {
    q: "Gibt es einen unabhängigen Erfahrungsbericht zum heilys Gen30+ Vision?",
    a: "Ja. Benedikt Keck hat das Gen30+ Vision rund 50 Tage im Alltag getestet und seine Erfahrungen in einem Videobericht oben auf dieser Seite zusammengefasst: Vision-Modus mit 670 nm morgens für die Augen (bei chronischer Meibom-Drüsen-Dysfunktion), Ambient-Light-Modus am Abend als Teil der Lichthygiene und Kombi-Modus für gezielte Anwendungen an Gesicht, Nacken und Muskulatur. Das Video ordnet auch die Scientific-Reports-Studie zu 670 nm und Netzhautfunktion ein.",
  },
  {
    q: "Wie unterscheidet sich das Gen30+ Vision vom Standard-Gen30?",
    a: "Das Gen30+ Vision ergänzt das Standard-Gen30 um den Vision-Modus mit 670 nm. Dieser Sondermodus ist laut heilys speziell für die Anwendung im Augenbereich gedacht und kann nach Herstellerangaben ohne Schutzbrille genutzt werden. Im Kombi- und Pulsmodus wird die mitgelieferte Schutzbrille hingegen empfohlen.",
  },
  {
    q: "Welche Wellenlängen hat das Gerät genau?",
    a: "Sechs Wellenlängen: 630, 660 und 670 nm im sichtbaren Rotbereich sowie 810, 830 und 850 nm im Nahinfrarot-Bereich (NIR). Die Kombination von Rot- und NIR-Licht in einem kompakten Gerät ist eine der wichtigsten Stärken des Gen30+ Vision.",
  },
  {
    q: "Was kostet das Gen30+ Vision mit dem Code LONGEVITY?",
    a: `Die UVP liegt bei 199 €, der Straßenpreis im heilys Shop bei rund 159 €. Mit dem Rabattcode ${DISCOUNT_CODE} sparst du zusätzlich ${DISCOUNT_PERCENT} % im Checkout. Den finalen Preis und die aktuellen Konditionen prüfst du am besten direkt im heilys Shop.`,
  },
  {
    q: "Funktioniert der Code LONGEVITY auch auf das Gen30+ Vision?",
    a: `Ja, der Rabattcode ${DISCOUNT_CODE} gilt nach aktuellem Stand auf das gesamte heilys Sortiment, also auch auf das Gen30+ Vision. Du gibst ihn im Checkout ein und erhältst ${DISCOUNT_PERCENT} % Rabatt direkt vom Bestellwert abgezogen.`,
  },
  {
    q: "Wie lange sollte ich den Vision-Modus pro Tag nutzen?",
    a: "Bene nutzt den Vision-Modus morgens für rund 3 bis 5 Minuten, einmal täglich, ohne direkt in die LEDs zu schauen. Mehr ist hier nicht automatisch besser – die zugrundeliegende Studienlage arbeitet mit kurzen Expositionen im Minutenbereich. Das ist ein persönlicher Erfahrungswert, keine medizinische Empfehlung. Bei Vorerkrankungen oder Unsicherheit gilt: vorher mit dem Augenarzt sprechen.",
  },
  {
    q: "Brauche ich zusätzlich ein Full-Body-Panel?",
    a: "Das Gen30+ Vision ist für Punktanwendungen an Gesicht, Nacken, Muskeln und Gelenken ausgelegt – nicht für großflächige Ganzkörper-Sessions. Wer Beine, Rücken und Oberkörper gleichzeitig bestrahlen möchte, ergänzt sinnvollerweise ein größeres Panel wie das heilys FullBody Max. Für die meisten Alltagsroutinen reicht das Vision allein.",
  },
  {
    q: "Ist das Gen30+ Vision ein Medizinprodukt?",
    a: "Nein. heilys weist selbst darauf hin, dass die Geräte nicht als Medizinprodukte klassifiziert sind. Die Inhalte auf heilys.info dienen ausschließlich der allgemeinen Information und ersetzen keine medizinische Beratung.",
  },
  {
    q: "Wo kaufe ich das Gerät am sichersten?",
    a: "Am besten direkt im offiziellen heilys Shop. Marktplätze und Drittanbieter sind häufig kein autorisierter Vertrieb – Garantie- und Kulanzfälle laufen sauberer, wenn du beim Hersteller bestellst.",
  },
];

const Gen30Vision = () => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(DISCOUNT_CODE);
    setCopied(true);
    window.lh?.trackCodeCopy({ source: "gen30_vision" });
    setTimeout(() => setCopied(false), 2000);
  };

  const breadcrumb = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Startseite", item: "https://heilys.info/" },
      { "@type": "ListItem", position: 2, name: "heilys Gen30+ Vision", item: CANONICAL },
    ],
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: FAQ.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  // Article-Schema (Review-Artikel) — sauberer Weg ohne erfundene Ratings.
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "heilys Gen30+ Vision: Specs, Erfahrungen & Code LONGEVITY",
    description:
      "Unabhängige Einordnung des heilys Gen30+ Vision: 6 Wellenlängen, Vision-Modus 670 nm, 50-Tage-Erfahrungsbericht und aktueller Rabattcode.",
    mainEntityOfPage: CANONICAL,
    datePublished: "2026-04-01",
    dateModified: "2026-05-25",
    inLanguage: "de",
    author: {
      "@type": "Person",
      name: "Benedikt Keck",
      url: "https://www.longevity-hackers.de/about",
    },
    publisher: {
      "@type": "Organization",
      name: "heilys.info",
      url: "https://heilys.info/",
      logo: {
        "@type": "ImageObject",
        url: "https://heilys.info/favicon.png",
      },
    },
  };

  // Product-Schema ohne Rating/Price – signalisiert Google die Entität "heilys Gen30+ Vision"
  // und hilft beim Ranking für Brand-Produkt-Queries wie "heilys gen30".
  const productSchema = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: "heilys Gen30+ Vision",
    brand: { "@type": "Brand", name: "heilys" },
    category: "Rotlichttherapie-Gerät",
    description:
      "Kompaktes Rotlicht- und Nahinfrarot-Gerät mit 36 Dual-Chip-LEDs, 30 W, sechs Wellenlängen (630/660/670/810/830/850 nm) und drei Betriebsmodi inkl. 670-nm-Vision-Modus.",
    image: [
      "https://heilys.info/images/heilys_3.webp",
      "https://heilys.info/images/heilys_4.webp",
    ],
    url: CANONICAL,
    sku: "heilys-gen30-vision",
    mpn: "GEN30-VISION",
    manufacturer: {
      "@type": "Organization",
      name: "weCare+ GmbH",
      address: { "@type": "PostalAddress", addressLocality: "Bielefeld", addressCountry: "DE" },
    },
    offers: {
      "@type": "Offer",
      url: PARTNER_LINK,
      priceCurrency: "EUR",
      price: "159.00",
      priceValidUntil: "2026-12-31",
      availability: "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
      seller: { "@type": "Organization", name: "heilys (weCare+ GmbH)" },
    },
  };

  // VideoObject-Schema für den Erfahrungsbericht — ermöglicht Video-Rich-Results in der Google SERP.
  const videoSchema = YOUTUBE_VIDEO_ID
    ? {
        "@context": "https://schema.org",
        "@type": "VideoObject",
        name: "heilys Gen30+ Vision Erfahrungsbericht: 50 Tage Vision-Modus 670 nm im Alltagstest",
        description:
          "Persönlicher Erfahrungsbericht von Benedikt Keck nach 50 Tagen mit der heilys Gen30+ Vision: Vision-Modus 670 nm für die Augen (chronische Meibom-Drüsen-Dysfunktion), Ambient-Light-Modus für die Abendroutine und klassische Photobiomodulation für Regeneration, Haut und Wohlbefinden. Inkl. wissenschaftlichem Kontext zur Scientific-Reports-Studie zu 670 nm und Netzhautfunktion.",
        thumbnailUrl: [`https://i.ytimg.com/vi/${YOUTUBE_VIDEO_ID}/maxresdefault.jpg`],
        uploadDate: "2026-05-25T10:00:00+02:00",
        duration: "PT7M35S",
        contentUrl: `https://youtu.be/${YOUTUBE_VIDEO_ID}`,
        embedUrl: `https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}`,
        publisher: {
          "@type": "Organization",
          name: "heilys.info",
          url: "https://heilys.info/",
        },
        inLanguage: "de",
      }
    : null;

  // Article mit Video-Referenz koppeln, wenn vorhanden.
  const articleSchemaWithVideo = videoSchema
    ? { ...articleSchema, video: videoSchema }
    : articleSchema;

  const schemas: Record<string, unknown>[] = [breadcrumb, articleSchemaWithVideo, productSchema];
  if (videoSchema) schemas.push(videoSchema);
  schemas.push(faqSchema);

  return (
    <Layout>
      <SEOHead
        title="heilys Gen30+ Vision Erfahrungen & Test 2026 – Rotlichttherapie kompakt"
        description="heilys Gen30+ Vision im 50-Tage-Test: Erfahrungen mit Vision-Modus 670 nm, 6 Wellenlängen, 36 Dual-Chip-LEDs. Unabhängige Einordnung – optional 5 % mit LONGEVITY."
        canonical={CANONICAL}
        ogType="article"
        ogImageAlt="heilys Gen30+ Vision Erfahrungen & Test 2026 – Rotlichttherapie"
        schemaJson={schemas}
      />


      <article className="container py-12 md:py-20">
        <div className="mx-auto max-w-3xl">
          {/* Breadcrumb */}
          <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
            <Link to="/" className="hover:text-foreground">Startseite</Link>
            <span className="mx-2">›</span>
            <span className="text-foreground">heilys Gen30+ Vision</span>
          </nav>

          <h1 className="mt-4 font-display text-3xl font-bold text-foreground sm:text-4xl">
            heilys Gen30+ Vision — Specs, Erfahrungen &amp; Rabattcode {DISCOUNT_CODE}
          </h1>
          <p className="mt-4 text-sm leading-relaxed text-muted-foreground sm:text-base">
            Das Gen30+ Vision ist heilys' kompaktes Rotlicht- und Nahinfrarot-Gerät für gezielte Anwendungen
            an Gesicht, Nacken, Muskeln und Gelenken. Die Besonderheit: Sechs Wellenlängen in einem Gerät und
            ein eigener 670-nm-Vision-Modus. Hier findest du die wichtigsten Specs, eine unabhängige Einordnung
            und den aktuellen Rabattcode {DISCOUNT_CODE} für 5 % auf den Gerätepreis.
          </p>

          {/* CTA-Box ganz oben (Above the Fold) */}
          <div className="mt-8 rounded-2xl border border-primary/20 bg-card p-5 sm:p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="font-display text-base font-semibold text-foreground">
                  {DISCOUNT_PERCENT} % Rabatt mit Code
                </p>
                <p className="mt-1 text-xs text-muted-foreground">
                  Im heilys Shop einlösen · Preis bleibt für dich gleich
                </p>
              </div>
              <div className="flex flex-col items-stretch gap-2 sm:flex-row sm:items-center">
                <button
                  type="button"
                  onClick={handleCopy}
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-4 py-2.5 font-display text-base font-bold tracking-wider text-primary transition-colors hover:bg-secondary"
                  aria-label={`Rabattcode ${DISCOUNT_CODE} kopieren`}
                >
                  {DISCOUNT_CODE}
                  {copied ? (
                    <Check className="h-4 w-4 text-success" aria-hidden="true" />
                  ) : (
                    <Copy className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                  )}
                </button>
                <a
                  href={PARTNER_LINK}
                  target="_blank"
                  rel="sponsored noopener noreferrer"
                  onClick={() => window.lh?.trackAffiliate({ source: "gen30_vision_top" })}
                  className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/20 transition-all hover:brightness-110"
                >
                  Zum heilys Shop
                  <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
                </a>
              </div>
            </div>
          </div>

          {/* Video-Embed (erscheint, sobald YOUTUBE_VIDEO_ID gesetzt ist) */}
          {YOUTUBE_VIDEO_ID && (
            <section className="mt-10">
              <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">
                heilys Gen30+ Vision – Erfahrungsbericht nach 50 Tagen (Video)
              </h2>
              <div className="mt-4 aspect-video overflow-hidden rounded-2xl border border-border bg-card">
                <iframe
                  src={`https://www.youtube-nocookie.com/embed/${YOUTUBE_VIDEO_ID}`}
                  title="heilys Gen30+ Vision Erfahrungsbericht nach 50 Tagen – Vision-Modus 670 nm im Alltagstest"
                  loading="lazy"
                  allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="h-full w-full"
                />
              </div>
              <p className="mt-3 text-xs leading-relaxed text-muted-foreground sm:text-sm">
                Persönlicher 50-Tage-Test: Vision-Modus mit 670 nm Rotlicht morgens bei chronischer
                Meibom-Drüsen-Dysfunktion, Ambient-Light-Modus für die Abendroutine und klassische
                Photobiomodulation für Gesicht, Nacken und Muskulatur. Inkl. Einordnung der
                Scientific-Reports-Studie zu 670 nm und Netzhautfunktion.
              </p>
            </section>
          )}


          {/* Auf einen Blick / Spec-Box */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">Auf einen Blick</h2>
            <div className="mt-4 overflow-hidden rounded-xl border border-border bg-card">
              <dl className="divide-y divide-border/60">
                {SPECS.map((s) => (
                  <div key={s.label} className="grid grid-cols-2 gap-3 px-4 py-3 text-sm sm:px-5">
                    <dt className="text-muted-foreground">{s.label}</dt>
                    <dd className="text-right font-medium text-foreground">{s.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Quelle: offizielle heilys Produktseite und Datenblatt. Angaben können sich ändern – im Zweifel im
              heilys Shop prüfen.
            </p>
          </section>

          {/* Was unterscheidet Vision vom Standard-Gen30 */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Was unterscheidet das Gen30+ Vision vom Standard-Gen30?
            </h2>
            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              {[
                { icon: Eye, label: "670-nm-Vision-Modus", text: "Eigener Modus für die Anwendung im Augenbereich – laut Hersteller ohne Schutzbrille nutzbar." },
                { icon: Waves, label: "6 statt 5 Wellenlängen", text: "Die zusätzliche 670-nm-Linie ergänzt das Standard-Spektrum aus 630/660/810/830/850 nm." },
                { icon: Zap, label: "3 Betriebsmodi", text: "Kombi-, Puls- und Vision-Modus – jeweils für unterschiedliche Anwendungsszenarien." },
                { icon: Shield, label: "Sonst baugleich", text: "Gehäuse, Leistung (30 W), 36 Dual-Chip-LEDs und Low-EMF-Charakteristik bleiben identisch." },
              ].map((d) => (
                <div key={d.label} className="rounded-xl border border-border/60 bg-card p-4">
                  <div className="flex items-center gap-2">
                    <span className="inline-flex items-center justify-center rounded-lg bg-primary/10 p-1.5">
                      <d.icon className="h-4 w-4 text-primary" aria-hidden="true" />
                    </span>
                    <h3 className="font-display text-sm font-semibold text-foreground">{d.label}</h3>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-muted-foreground sm:text-sm">{d.text}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Wellenlängen-Tabelle */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Die 6 Wellenlängen im Detail
            </h2>
            <p className="mt-2 text-sm text-muted-foreground">
              Die Kombination aus Rot- und Nahinfrarot-Licht ist das, was das Gen30+ Vision von vielen
              Single-Wellenlängen-Geräten unterscheidet.
            </p>
            <div className="mt-4 overflow-hidden rounded-xl border border-border bg-card">
              <div className="grid grid-cols-12 border-b border-border bg-secondary/40 px-4 py-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground sm:px-5">
                <div className="col-span-3">Wellenlänge</div>
                <div className="col-span-2">Bereich</div>
                <div className="col-span-7">Hinweis</div>
              </div>
              {WAVELENGTHS.map((w) => (
                <div key={w.nm} className="grid grid-cols-12 items-center border-b border-border/60 px-4 py-3 text-sm last:border-b-0 sm:px-5">
                  <div className="col-span-3 font-medium text-foreground">{w.nm}</div>
                  <div className="col-span-2 text-xs text-muted-foreground">{w.range}</div>
                  <div className="col-span-7 text-xs text-muted-foreground sm:text-sm">{w.note}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Vision-Modus */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Der Vision-Modus (670 nm)
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Der Vision-Modus ist das Alleinstellungsmerkmal der Vision-Variante. heilys nennt 670 nm als
              Wellenlänge speziell für die Anwendung im Augenbereich und gibt an, dass dieser Modus ohne
              Schutzbrille genutzt werden kann. Im Kombi- und Pulsmodus empfiehlt der Hersteller dagegen
              ausdrücklich, die mitgelieferte Schutzbrille zu tragen.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Wissenschaftlicher Kontext: Eine kleine, kontrollierte Studie aus <em>Scientific Reports</em>{" "}
              (Shinhmar et al., 2020) untersuchte den Effekt einer einmaligen, dreiminütigen Exposition mit
              670-nm-Licht am Morgen auf die Farbkontrast-Wahrnehmung. Im Schnitt verbesserte sich die
              Farbkontrast-Sensitivität auf der Tritan-Achse um rund 17 % und auf der Protan-Achse um etwa
              12 % – gemessen drei Stunden nach der Exposition, mit Effekten, die bis zu eine Woche später
              noch nachweisbar waren. Untersucht wurde Netzhautfunktion, nicht Heilung. Eine Studie mit
              kleiner Stichprobe ist kein Beweis – aber der Mechanismus rund um mitochondriale Funktion und
              ATP-Produktion in der hochenergetischen Netzhaut ist plausibel und wird aktuell aktiv beforscht.
            </p>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Wichtig: Wir machen hier keine medizinischen Aussagen. Wenn du den Vision-Modus für die Augen
              nutzen möchtest und unsicher bist, sprich vorher mit deinem Augenarzt – speziell bei
              Vorerkrankungen, hoher Lichtempfindlichkeit oder nach Augenoperationen.
            </p>
          </section>

          {/* Wer steht hinter heilys? */}
          <section className="mt-12 rounded-2xl border border-border bg-card p-5 sm:p-6">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Wer steht hinter heilys?
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              heilys ist eine deutsche Marke aus Bielefeld. Gründer und Geschäftsführer ist{" "}
              <strong className="text-foreground">Yike Pan</strong>, Lichtwissenschaftler mit fachlichem
              Hintergrund in Photobiomodulation – also kein Quereinsteiger, der auf den Rotlicht-Hype
              aufspringt, sondern jemand, der die Materie inhaltlich versteht. Das ist mit einer der Gründe,
              warum wir uns die Geräte überhaupt ausführlich angeschaut haben.
            </p>
          </section>

          {/* Anwendung */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Anwendung &amp; Abstand
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Das Gen30+ Vision ist auf den Nahbereich ausgelegt: 16° Abstrahlwinkel und bis zu 95 mW/cm²
              Bestrahlungsstärke bei 7,5 cm Abstand machen es zum klassischen Punkt-Gerät für Gesicht, Nacken,
              Muskeln und Gelenke – nicht für Ganzkörper-Setups. Konkrete Empfehlungen zu Sitzungsdauer,
              Häufigkeit und Abstand findest du in der heilys Bedienungsanleitung; sie ändern sich je nach
              Modus und Anwendungsbereich.
            </p>
          </section>

          {/* 50 Tage im Alltag — Erfahrungsbericht */}
          <section className="mt-12 rounded-2xl border border-primary/20 bg-card p-5 sm:p-6">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              50 Tage im Alltag — Bene's Erfahrungsbericht
            </h2>
            <p className="mt-2 text-xs uppercase tracking-wider text-muted-foreground">
              Persönlicher Eindruck · keine medizinische Empfehlung
            </p>

            <h3 className="mt-5 font-display text-base font-semibold text-foreground">
              Warum gerade die Gen30+Vision?
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Bei mir zu Hause stehen mittlerweile mehrere Rotlicht-Geräte: eine Maske von Koanna, ein
              kleines Panel von BESTQOOL und das große Flowlight Panel 4300 von Flowlife. Alle haben ihre
              Berechtigung – aber ehrlich gesagt: Ich bekomme sie schlecht in den Alltag eingebaut. Die Maske
              überzeugt mich nicht mehr, das Full-Body-Panel ist für eine schnelle Routine viel zu wuchtig.
              Genau diese Lücke schließt das Gen30+ Vision für mich.
            </p>

            <h3 className="mt-5 font-display text-base font-semibold text-foreground">
              Morgens: Vision-Modus für die Augen
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Ich habe eine chronische Meibom-Drüsen-Dysfunktion – Augen, die schnell trocken, gereizt und
              lichtempfindlich werden. Morgens nutze ich den Vision-Modus mit 670 nm rund 5 Minuten und
              schaue dabei <em>um</em> die Lampe herum, nicht direkt in die LEDs. Das ist meine Routine,
              ergänzend zu allem, was mein Augenarzt empfiehlt – kein Ersatz dafür.
            </p>

            <h3 className="mt-5 font-display text-base font-semibold text-foreground">
              Abends: Ambient-Light statt OP-Saal
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Unser Deckenlicht ist abends einfach unangenehm grell. Die heilys-Lampe läuft daher häufig im
              gedimmten Ambient-Light-Modus als sanftes rotes Umgebungslicht. Das passt zur Lichthygiene am
              Abend: Helles, blaustichiges Licht hält wach, weiches Rotlicht stört den circadianen Rhythmus
              weniger – kein Schlaf-Zauber, einfach bessere Licht-Hygiene.
            </p>

            <h3 className="mt-5 font-display text-base font-semibold text-foreground">
              Was ich nach 50 Tagen sage
            </h3>
            <ol className="mt-2 list-decimal space-y-2 pl-5 text-sm leading-relaxed text-muted-foreground sm:text-base">
              <li>
                <strong className="text-foreground">Die Lampe bleibt nicht ungenutzt.</strong> Das ist für
                mich der größte Punkt – das beste Gerät bringt nichts, wenn es im Schrank steht.
              </li>
              <li>
                <strong className="text-foreground">Meine Augen fühlen sich morgens subjektiv stabiler an.</strong>{" "}
                Weniger das „verquollene, gereizte" Gefühl. Persönlicher Eindruck, kein Messwert – ich bin
                dazu mit Augenärzten im Austausch und reiche objektivere Ergebnisse später nach.
              </li>
              <li>
                <strong className="text-foreground">Alltagstauglich.</strong> Fernbedienung, kompakte Form,
                lässt sich überall hinstellen, ohne dass es nach Therapiegerät aussieht.
              </li>
            </ol>

            <h3 className="mt-5 font-display text-base font-semibold text-foreground">
              Kritikpunkte
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              Es ist <em>keine</em> Full-Body-Lösung. Wer Beine, Rücken und Oberkörper gleichzeitig bestrahlen
              möchte, braucht ein großes Panel (z. B. das heilys FullBody Max). Und bei der Augen-Anwendung
              muss man diszipliniert bleiben: Abstand einhalten, 3 bis maximal 5 Minuten, nicht direkt in die
              LEDs schauen. Mehr ist hier nicht automatisch besser.
            </p>
          </section>


          {/* Erfahrungen Teaser */}
          <section className="mt-12 rounded-2xl border border-border bg-card p-5 sm:p-6">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Erste Erfahrungen &amp; Trustpilot
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              heilys wird auf Trustpilot mit 4,76 von 5 Sternen bewertet (Stand letzter Recherche). Käufer-
              berichte zum Gen30+ Vision heben in der Regel das kompakte Format, die saubere Verarbeitung
              und den Vision-Modus hervor. Eine ausführliche Übersicht zu Erfahrungen, Lieferzeiten und
              Garantie findest du auf der Startseite.
            </p>
            <Link
              to="/#erfahrungen"
              className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
            >
              Erfahrungen ansehen
              <span aria-hidden="true">→</span>
            </Link>
          </section>

          {/* Preis & Code (Sekundärer CTA) */}
          <section className="mt-12 rounded-2xl border border-primary/20 bg-card p-6 sm:p-8">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">
              Preis &amp; Rabattcode {DISCOUNT_CODE}
            </h2>
            <p className="mt-3 text-sm leading-relaxed text-muted-foreground sm:text-base">
              UVP 199 €, Straßenpreis im heilys Shop rund 159 €. Mit dem Code{" "}
              <strong className="text-foreground">{DISCOUNT_CODE}</strong> sparst du im Checkout zusätzlich{" "}
              {DISCOUNT_PERCENT} %. Der Code gilt auf das gesamte Sortiment – also auch auf Zubehör.
            </p>
            <div className="mt-5 flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
              <button
                type="button"
                onClick={handleCopy}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-border bg-card px-5 py-3 font-display text-lg font-bold tracking-wider text-primary transition-colors hover:bg-secondary"
                aria-label={`Rabattcode ${DISCOUNT_CODE} kopieren`}
              >
                {DISCOUNT_CODE}
                {copied ? (
                  <Check className="h-4 w-4 text-success" aria-hidden="true" />
                ) : (
                  <Copy className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
                )}
              </button>
              <a
                href={PARTNER_LINK}
                target="_blank"
                rel="sponsored noopener noreferrer"
                onClick={() => window.lh?.trackAffiliate({ source: "gen30_vision_bottom" })}
                className="inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/25 transition-all hover:brightness-110"
              >
                Gen30+ Vision im heilys Shop ansehen
                <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
              </a>
            </div>
            <p className="mt-3 text-xs text-muted-foreground">
              Partnerlink · Der Preis für dich bleibt unverändert.
            </p>
          </section>

          {/* FAQ */}
          <section className="mt-12">
            <h2 className="font-display text-xl font-bold text-foreground sm:text-2xl">Häufige Fragen</h2>
            <div className="mt-4 space-y-3">
              {FAQ.map((f) => (
                <details
                  key={f.q}
                  className="group rounded-xl border border-border bg-card p-4 open:border-primary/30"
                >
                  <summary className="cursor-pointer list-none font-display text-sm font-semibold text-foreground sm:text-base">
                    <span className="flex items-start justify-between gap-3">
                      <span>{f.q}</span>
                      <span className="text-muted-foreground transition-transform group-open:rotate-45" aria-hidden="true">+</span>
                    </span>
                  </summary>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{f.a}</p>
                </details>
              ))}
            </div>
          </section>

          {/* Footer-Verlinkung */}
          <p className="mt-12 text-sm text-muted-foreground">
            Mehr Hintergründe zum Anbieter und zur Finanzierung dieser Seite findest du auf{" "}
            <Link to="/transparenz" className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
              Transparenz
            </Link>
            . Zurück zum{" "}
            <Link to="/" className="font-medium text-foreground underline underline-offset-2 hover:text-primary">
              heilys Rabattcode LONGEVITY
            </Link>
            .
          </p>
        </div>
      </article>
    </Layout>
  );
};

export default Gen30Vision;
